/**
 * Authentication API service
 * Handles login requests to the backend
 */

import type { LoginRequest, LoginResponse, ApiResult } from '@/types/api';

// Configuration from environment
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';
const REQUEST_TIMEOUT = parseInt(import.meta.env.VITE_REQUEST_TIMEOUT || '30000', 10);

/**
 * Call the login endpoint
 */
export async function login(
  credentials: LoginRequest
): Promise<ApiResult<LoginResponse>> {
  const url = `${API_BASE_URL}/api/v1/login`;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(credentials),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    // Try to parse response body
    let data: LoginResponse | undefined;
    let errorMessage: string | undefined;

    const contentType = response.headers.get('content-type');
    if (contentType?.includes('application/json')) {
      try {
        const json = await response.json();
        if (response.ok) {
          data = json as LoginResponse;
        } else {
          // Error response - extract message
          errorMessage = json.detail || json.message || json.title || 'Login failed';
          if (typeof errorMessage === 'object') {
            errorMessage = JSON.stringify(errorMessage);
          }
        }
      } catch {
        errorMessage = 'Failed to parse response JSON';
      }
    } else {
      const text = await response.text();
      if (!response.ok) {
        errorMessage = text || `HTTP ${response.status}`;
      }
    }

    if (response.ok && data) {
      return {
        ok: true,
        data,
      };
    }

    return {
      ok: false,
      error: errorMessage || `HTTP ${response.status}`,
    };
  } catch (error) {
    clearTimeout(timeoutId);

    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        return {
          ok: false,
          error: 'Request timed out',
        };
      }
      return {
        ok: false,
        error: error.message || 'Network error',
      };
    }

    return {
      ok: false,
      error: 'Unknown error occurred',
    };
  }
}
